import React, { forwardRef, useMemo } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  FormLabelProps,
  useRadio,
  useRadioGroup,
} from "@chakra-ui/react";
import { boolify, boolString, compose, Option } from "@/utils/formUtils";
import { useTranslation } from "react-i18next";

export type RadioCardProps = {
  name: string;
  value: string;
  size?: "sm" | "md" | "lg" | "xl";
  disabled?: boolean;
  children: React.ReactNode;
  dataTestId: string;
};

const RadioCard: React.FC<RadioCardProps> = forwardRef<HTMLLabelElement, RadioCardProps>(
  (props, ref) => {
    const { getInputProps, getRadioProps } = useRadio(props);
    const { size = "md", disabled, dataTestId } = props;

    const input = getInputProps();
    const checkbox = getRadioProps();

    return (
      <Box
        width={size === "sm" ? "fit-content" : ["100%", "45%"]}
        height={size === "xl" ? [20, 36, 48] : "fit-content"}
        flexGrow={size === "sm" ? 0 : 1}
        as="label"
      >
        {!disabled && <input {...input} />}
        <Flex
          height="full"
          fontSize="xl"
          alignItems="center"
          justifyContent={size === "md" ? "flex-start" : "center"}
          {...checkbox}
          cursor={disabled ? "not-allowed" : "pointer"}
          borderWidth="1px"
          borderColor="gray.500"
          color={disabled ? "gray.500" : "inherit"}
          borderRadius="md"
          _hover={{
            borderColor: disabled ? "gray.300" : "blue.500",
          }}
          _checked={{
            bg: disabled ? "gray.50" : "blue.50",
            color: disabled ? "black" : "blue.700",
            borderColor: disabled ? "gray.900" : "blue.500",
            outline: "1px solid",
            outlineColor: disabled ? "gray.900" : "blue.500",
            outlineOffset: "0",
          }}
          _focus={{
            outline: "2px solid",
            outlineColor: "blue.500",
            outlineOffset: "2px",
          }}
          px={5}
          py={3}
          data-testid={dataTestId}
          aria-hidden="false"
        >
          {props.children}
        </Flex>
      </Box>
    );
  },
);

type RadioCardOption = string | Option;

export type RadioCardsProps = {
  name: string;
  options: RadioCardOption[];
  value?: string;
  onChange: (value: string) => void;
  size?: "sm" | "md" | "lg" | "xl";
  disabled?: boolean;
  dataTestPrefix?: string;
  isInvalid?: boolean;
  label?: string | React.ReactNode;
  labelProps?: FormLabelProps;
  isRequired?: boolean;
  helperText?: string | React.ReactNode;
  onBlur?: () => void;
};

export const RadioCards: React.FC<RadioCardsProps> = forwardRef((props, ref) => {
  const {
    name,
    options,
    value,
    onChange,
    size,
    disabled,
    label,
    labelProps,
    isRequired,
    helperText,
    onBlur,
  } = props;
  const dataTestPrefix = props.dataTestPrefix ?? name;

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: name,
    value: value,
    onChange: onChange,
  });

  const group = getRootProps();

  const radioCards = (
    <Flex
      flexWrap="wrap"
      direction={size === "sm" ? "row" : ["column", "row"]}
      width="full"
      gap={4}
      {...group}
      onBlur={onBlur}
    >
      {options.map((optionValue) => {
        const optionLabel = typeof optionValue === "string" ? optionValue : optionValue.label;
        const optionValueStr = typeof optionValue === "string" ? optionValue : optionValue.value;

        const radio = getRadioProps({ value: optionValueStr });
        return (
          <RadioCard
            disabled={disabled}
            name={name}
            value={optionValueStr}
            size={size}
            key={optionValueStr}
            dataTestId={`${dataTestPrefix}-${optionValueStr}`}
            {...radio}
          >
            {optionLabel}
          </RadioCard>
        );
      })}
    </Flex>
  );

  // If label is provided, wrap in fieldset with legend
  if (label) {
    return (
      <FormControl
        as="fieldset"
        isRequired={isRequired}
        border="none"
        padding={0}
        margin={0}
        width="full"
      >
        <FormLabel as="legend" fontSize="lg" marginBlockEnd={2} fontWeight="medium" {...labelProps}>
          {label}
        </FormLabel>
        {radioCards}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  // Otherwise just return the radio cards (backward compatible)
  return radioCards;
});

export type BoolRadioCardsProps = {
  name: string;
  value?: boolean | null | undefined;
  onChange: (value: boolean | null | undefined) => void;
  size?: "sm" | "md" | "lg" | "xl";
  disabled?: boolean;
  dataTestPrefix?: string;
  label?: string | React.ReactNode;
  labelProps?: FormLabelProps;
  isRequired?: boolean;
  helperText?: string | React.ReactNode;
  onBlur?: () => void;
};

export const BoolRadioCards = forwardRef((props: BoolRadioCardsProps, ref) => {
  const { value, onChange, ...passThroughProps } = props;
  const { t } = useTranslation("common");

  const options: Option[] = useMemo(
    () => [
      { label: t("labels.yes"), value: "yes" },
      { label: t("labels.no"), value: "no" },
    ],
    [t],
  );

  return (
    <RadioCards
      {...passThroughProps}
      options={options}
      value={boolString(value)}
      onChange={compose(boolify, onChange)}
    />
  );
});
